import LoadingPage from "@components/Loader/LoadingPage";
import { EventType } from "@models/event";
import {
  Transaction,
  TransactionParam,
  TransactionStatus,
} from "@models/payment";
import { useUserStore } from "@providers/auth";
import * as Sentry from "@sentry/browser";
import { isEmpty } from "lodash";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { PaymentStep, resetCheckout, usePaymentStore } from "../payment";
import CheckoutOrderDetail, {
  CheckoutOrderDetailTitle,
} from "./CheckoutOrder/CheckoutOrderDetail/CheckoutOrderDetail";
import CheckoutOrderSelect, {
  CheckoutOrderSelectTitle,
} from "./CheckoutOrder/CheckoutOrderSelect/CheckoutOrderSelect";
import CheckoutSidebar from "./CheckoutSidebar/CheckoutSidebar";
import CheckoutToolbar from "./CheckoutToolbar/CheckoutToolbar";

const Checkout = () => {
  const router = useRouter();
  const user = useUserStore((state) => state.user);
  const event = usePaymentStore((state) => state.event);
  const transaction = usePaymentStore((state) => state.transaction);
  const currentStep = usePaymentStore((state) => state.currentStep);

  const [allowSubmit, setAllowSubmit] = useState(false);
  const [initializedStep, setInitializedStep] = useState(false);

  const selectedItems = useMemo(
    () => !isEmpty(transaction?.items),
    [transaction?.items.length]
  );

  useEffect(() => {
    Sentry.addBreadcrumb({
      category: "checkout:initialized",
      level: "info",
    });
  }, []);

  // Redirect completed transaction to success page
  useEffect(() => {
    if (!event || !transaction) return;
    console.log("checkout", event, transaction);
    switch (transaction.status) {
      case TransactionStatus.INITIAL: {
        return;
      }
      case TransactionStatus.FAILED: {
        router.push(`${window.location.pathname}/cancel`);
        return;
      }
      case TransactionStatus.SUCCEEDED: {
        router.push(`${window.location.pathname}/success`);
        return;
      }
      default: {
        router.push(window.location.pathname.replace(/\/checkout.+/, ""));
        return;
      }
    }
  }, [event, transaction?.status]);

  useEffect(() => {
    if (!transaction) return;
    console.log("transaction changed", transaction);
    Sentry.setTag("transaction.id", transaction.id);
    Sentry.setTag(
      "transaction.products",
      transaction.items.map((item) => item.name || item.productId).join(",")
    );
  }, [transaction]);

  useEffect(() => {
    if (!router.isReady || !router.query || isEmpty(router.query)) return;
    const _transaction = usePaymentStore.getState().transaction;
    if (!_transaction) return;
    // console.log(router.query);
    let updated = false;
    for (const key of Object.keys(router.query)) {
      if (key.startsWith("utm_")) {
        _transaction.metadata[key] = String(router.query[key]);
        updated = true;
      }
    }
    if (updated)
      usePaymentStore.setState({
        transaction: new Transaction(_transaction.json as any),
      });
  }, [router.isReady, router.query]);

  // Set userId for transaction
  useEffect(() => {
    console.log("user changed", user);
    const _transaction = usePaymentStore.getState().transaction;
    if (!user || !_transaction) return;
    Sentry.setUser({
      id: user.id,
      email: user.email,
    });
    if (!_transaction.userId) {
      _transaction.userId = user.id;
      if (user.email) _transaction.contact.email = user.email;
      if (user.username) _transaction.contact.name = user.username;
      if (user.walletAddresses.length == 1)
        _transaction.contact.wallet = user.walletAddresses[0];
      usePaymentStore.setState({
        transaction: new Transaction(_transaction.json as TransactionParam),
      });
    }
  }, [user]);

  useEffect(() => {
    if (!user || !transaction || initializedStep) return;
    setInitializedStep(true);
    if (
      user &&
      user.id === transaction.userId &&
      !isEmpty(transaction.items) &&
      usePaymentStore.getState().currentStep == PaymentStep.SELECT_ITEMS
    )
      usePaymentStore.setState({ currentStep: PaymentStep.CHECKOUT });
  }, [initializedStep, transaction, user]);

  useEffect(() => {
    if (!user || !transaction) return;
    if (transaction.userId && transaction.userId !== user.id) {
      console.log("not your transaction");
      resetCheckout();
      return;
    }
  }, [transaction, user]);

  if (!event || !transaction || transaction.status != TransactionStatus.INITIAL)
    return <LoadingPage />;

  const showAppNavBar = user && event.type === EventType.BENEFIT;

  return (
    <div
      className={`flex-grow w-full h-full relative ${
        showAppNavBar ? "" : "pt-8"
      }`}
    >
      <div className="flex flex flex-grow flex-col container">
        {user && selectedItems && currentStep == PaymentStep.CHECKOUT ? (
          <CheckoutOrderDetailTitle />
        ) : (
          <CheckoutOrderSelectTitle />
        )}
        <div className="container flex flex-col sm:flex-row flex-grow pb-[8rem] sm:py-5 px-4 lg:px-0 min-h-full gap-8 sm:items-start">
          <div className="flex-grow px-4 pt-8 sm:p-0 ">
            {user && selectedItems && currentStep == PaymentStep.CHECKOUT ? (
              <CheckoutOrderDetail setAllowSubmit={setAllowSubmit} />
            ) : (
              <CheckoutOrderSelect />
            )}
          </div>
          <CheckoutSidebar event={event} allowSubmit={allowSubmit} />
        </div>
      </div>
      <CheckoutToolbar allowSubmit={allowSubmit} />
    </div>
  );
};

export default Checkout;
